import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Calendar, Search, ChevronDown, ChevronLeft, ChevronRight, LayoutGrid, Tag } from 'lucide-react';
import Footer from '../components/Footer';

// Debounce Utility Hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

const ITEMS_PER_PAGE = 12;

// Helper to extract text from HTML (outside component to avoid re-creation)
const extractTextFromHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const text = doc.body.textContent || "";
    return text.length > 100 ? text.substring(0, 100) + '...' : text;
};

// Memoized Blog Card Component
const BlogCard = React.memo(({ blog, i18n }) => (
    <Link to={`/blog/${blog.slug}`} className="group relative flex flex-col bg-slate-50 rounded-[2.5rem] overflow-hidden transition-colors duration-500 isolate">
        {/* CIRCULAR BACKGROUND FILL ANIMATION */}
        <div
            className="absolute top-0 left-0 w-[400%] aspect-square rounded-full -translate-x-1/2 -translate-y-1/2 scale-0 group-hover:scale-100 transition-transform duration-700 ease-out z-0 will-change-transform"
            style={{
                background: 'radial-gradient(circle, #00a4ff 0%, #00a4ff 85%, transparent 100%)',
                filter: 'blur(30px)'
            }}
        />

        {/* 1. IMAGE CONTAINER */}
        <div className="relative w-full aspect-[4/3] overflow-hidden z-10">
            <div
                className="block w-full h-full relative z-10"
                style={{
                    maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
                    transform: 'translate3d(0,0,0)',
                    backfaceVisibility: 'hidden'
                }}
            >
                {blog.featured_image ? (
                    <img
                        src={blog.featured_image}
                        alt={blog.title}
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="300"
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-out"
                    />
                ) : (
                    <div className="w-full h-full bg-slate-200 flex items-center justify-center text-slate-300 group-hover:bg-white/10 group-hover:text-white/20 transition-colors">
                        <span className="text-4xl">🖼️</span>
                    </div>
                )}
            </div>

            <div className="absolute top-4 left-4 z-10">
                <span className="bg-white/90 backdrop-blur-sm text-slate-900 text-[10px] font-extrabold px-3 py-1.5 rounded-full shadow-md uppercase tracking-wider">
                    {new Date(blog.published_at || blog.created_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'id-ID', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
            </div>

            <div className="absolute top-4 right-4 z-10">
                <span className="bg-white/90 backdrop-blur-sm text-slate-900 text-[10px] font-extrabold px-3 py-1.5 rounded-full shadow-md uppercase tracking-wider">
                    {blog.category ? blog.category.name : 'Article'}
                </span>
            </div>
        </div>

        {/* 2. CONTENT CONTAINER */}
        <div className="relative px-6 pb-8 pt-12 flex flex-col items-center flex-1 z-20 -mt-12">
            <h2 className="text-center text-xl font-extrabold text-slate-900 mb-3 leading-snug group-hover:text-white transition-colors line-clamp-3 px-2">
                {blog.title}
            </h2>
            <p className="text-center text-slate-500 text-sm leading-relaxed line-clamp-2 number-of-lines-2 group-hover:text-blue-50/80 transition-colors px-2">
                {extractTextFromHtml(blog.excerpt || blog.content)}
            </p>
        </div>
    </Link>
));

const BlogListPage = () => {
    const { t, i18n } = useTranslation();
    // STATE
    const [blogs, setBlogs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    // FILTER STATE
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 500); // 500ms debounce
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // 1. Fetch Categories (Once)
    useEffect(() => {
        const fetchCategories = async () => {
            const { data } = await supabase.from('blog_categories').select('*').order('name');
            setCategories(data || []);
        };
        fetchCategories();
    }, []);

    // 2. Fetch Blogs (On Filter Change)
    useEffect(() => {
        const fetchBlogs = async () => {
            setLoading(true);

            // Build Query
            let query = supabase
                .from('blogs')
                .select('*, category:blog_categories(name, slug)', { count: 'exact' })
                .eq('status', 'published')
                .order('created_at', { ascending: false });

            // Apply Category Filter
            if (activeCategory !== 'all') {
                query = query.eq('category_id', activeCategory);
            }

            // Apply Search Filter (Live Search)
            if (debouncedSearch) {
                query = query.ilike('title', `%${debouncedSearch}%`);
            }

            // Apply Pagination (0-9, 10-19, etc.)
            const from = (currentPage - 1) * ITEMS_PER_PAGE;
            const to = from + ITEMS_PER_PAGE - 1;
            query = query.range(from, to);

            const { data, count, error } = await query;

            if (!error) {
                setBlogs(data || []);
                setTotalCount(count || 0);
            }
            setLoading(false);
        };

        fetchBlogs();
    }, [debouncedSearch, activeCategory, currentPage]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, activeCategory]);

    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <Helmet>
                <title>{t('blogPage.title')} {t('blogPage.titleHighlight')} - Neo AI</title>
                <meta name="description" content={t('blogPage.description')} />

                {/* Structured Data for SEO */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "CollectionPage",
                        "headline": `${t('blogPage.title')} ${t('blogPage.titleHighlight')} - Neo AI`,
                        "description": t('blogPage.description'),
                        "url": "https://neoai.id/blog",
                        "publisher": {
                            "@type": "Organization",
                            "name": "NeoAI",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://neoai.id/icon.svg"
                            }
                        }
                    })}
                </script>
                <link rel="canonical" href="https://neoai.id/blog" />
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16 grow w-full">

                {/* HEADER SECTION */}
                <div className="text-center mb-12 mt-10">
                    <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">
                        {t('blogPage.title')} <span className="text-[#2299ED]">{t('blogPage.titleHighlight')}</span>
                    </h1>
                    <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                        {t('blogPage.description')}
                    </p>
                </div>

                {/* CONTROLS BAR (Search & Filter) */}
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-10 shadow-sm">
                    <div className="flex flex-col md:flex-row gap-4 items-center justify-between">

                        {/* SEARCH INPUT (Live Search) */}
                        <div className="relative w-full md:w-96 group">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-5 w-5 text-slate-400 group-focus-within:text-sky-500 transition-colors" />
                            </div>
                            <input
                                type="text"
                                placeholder={t('blogPage.searchPlaceholder')}
                                aria-label="Search articles"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="block w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none transition-all placeholder:text-slate-400"
                            />
                        </div>

                        {/* CATEGORY DROPDOWN */}
                        <div className="relative w-full md:w-64">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <LayoutGrid className="h-4 w-4 text-slate-500" />
                            </div>
                            <select
                                value={activeCategory}
                                onChange={(e) => setActiveCategory(e.target.value)}
                                aria-label="Filter by category"
                                className="appearance-none block w-full pl-10 pr-10 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none cursor-pointer hover:border-slate-300 transition-colors"
                            >
                                <option value="all">{t('blogPage.allTopics')}</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <ChevronDown className="h-4 w-4 text-slate-500" />
                            </div>
                        </div>

                    </div>
                </div>

                {/* CONTENT AREA */}
                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="w-10 h-10 border-4 border-sky-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : blogs.length === 0 ? (
                    <div className="text-center py-20 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 text-slate-400 mb-4">
                            <Search size={24} />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 mb-1">{t('blogPage.noResultsTitle')}</h3>
                        <p className="text-slate-500">{t('blogPage.noResultsDesc')}</p>
                    </div>
                ) : (
                    <>
                        {/* NEW BLOG GRID DESIGN */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 mb-12">
                            {blogs.map(blog => (
                                <BlogCard key={blog.id} blog={blog} i18n={i18n} />
                            ))}
                        </div>

                        {/* PAGINATION CONTROLS */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-4 border-t border-slate-100 pt-8">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    aria-label="Previous page"
                                    className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-sky-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft size={20} />
                                </button>

                                <span className="text-sm font-medium text-slate-600">
                                    {t('blogPage.page')} <span className="text-slate-900 font-bold">{currentPage}</span> {t('blogPage.of')} {totalPages}
                                </span>

                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    aria-label="Next page"
                                    className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-sky-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default BlogListPage;
